import React, { useEffect, useContext, useMemo, useRef } from 'react';
import { Typography, Space, Tag, Tooltip, Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { useTable } from "@refinedev/antd";
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ColorModeContext } from '../../contexts/color-mode';
import { ChartIconButton } from './ChartIconButton';
import { MeterReadingsTable, type PersistedColumnConfig } from './MeterReadingsTable';
import { authenticatedFetch } from '../../utils';

dayjs.extend(relativeTime);

const { Text } = Typography;

// CSV export helper function
const handleCsvExport = async (concentratorID: string, media: string, month: string, concentratorName?: string) => {
    try {
        // Use authenticated fetch (includes 401/403 handling)
        const response = await authenticatedFetch(
            `/api/concentrators/${concentratorID}/meters/${media}/historical/export/?month=${month}`
        );

        if (!response.ok) {
            throw new Error('Export failed');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename with concentrator name
        let filename = `readouts_${media}_${month}.csv`;
        if (concentratorName) {
            const safeName = concentratorName.replace(/[^a-zA-Z0-9]/g, '');
            filename = `readouts_${media}_${month}_${safeName}.csv`;
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        message.success(`CSV export for ${month} completed successfully`);
    } catch (error) {
        message.error('Failed to export CSV');
    }
};

interface HistoricalReading {
    month: string;
    value: number | null;
    timestamp: string | null;
    is_missing: boolean;
    // For electricity meters
    active_value?: number | null;
    active_timestamp?: string | null;
    active_is_missing?: boolean;
    reactive_value?: number | null;
    reactive_timestamp?: string | null;
    reactive_is_missing?: boolean;
    // For heat meters
    value_kwh?: number | null;
    value_gj?: number | null;
    native_unit?: string;
}

interface MeterWithHistory {
    id: number;
    name: string;
    uuid: string;
    address?: string | null;
    serial?: string | null;
    is_shadow?: boolean | null;
    hot?: boolean | null;
    historical_readings: HistoricalReading[];
}

interface HistoricalReadingsTableProps {
    media: string;
    concentratorID: string;
    concentratorName?: string;
    searchValue: string;
}

// Helper function to render timestamp tooltip with relative to month end
const renderTimestampTooltip = (timestamp: string | null | undefined, month: string) => {
    if (!timestamp) return null;
    
    const readingDate = dayjs(timestamp);
    const monthDate = dayjs(month + '-01'); // Convert "2024-01" to date
    const endOfMonth = monthDate.endOf('month');
    
    // Calculate days from end of month
    const daysFromEnd = endOfMonth.diff(readingDate, 'day');
    const relativeToMonthEnd = daysFromEnd === 0 
        ? 'Last day of month'
        : daysFromEnd === 1 
        ? '1 day before month end'
        : `${daysFromEnd} days before month end`;
    
    return (
        <div>
            <div>{relativeToMonthEnd}</div>
            <div>{readingDate.format('D.M.YYYY HH:mm:ss')}</div>
        </div>
    );
};

// Helper function to render a reading cell with tooltip
const renderReadingCell = (
    value: number | null | undefined,
    timestamp: string | null | undefined,
    isMissing: boolean | undefined,
    unit: string,
    month: string,
    isNative: boolean = false,
    isDark: boolean = false
) => {
    if (!value || value === null) {
        return <Tag color="default">No Data</Tag>;
    }

    const valueDisplay = `${value.toFixed(2)} ${unit}`;
    
    // Calculate background color based on days from end of month and theme
    let backgroundColor = isDark ? '#1f3a1f' : '#f6ffed'; // dark green / light green
    
    if (timestamp) {
        const readingDate = dayjs(timestamp);
        const monthDate = dayjs(month + '-01');
        const endOfMonth = monthDate.endOf('month');
        const daysFromEnd = endOfMonth.diff(readingDate, 'day');
        
        if (daysFromEnd === 0) {
            backgroundColor = isDark ? '#1f3a1f' : '#f6ffed'; // dark green / light green
        } else if (daysFromEnd <= 3) {
            backgroundColor = isDark ? '#3a3010' : '#fffbe6'; // dark yellow / light yellow
        } else {
            backgroundColor = isDark ? '#3a1a1a' : '#fff2f0'; // dark red / light red
        }
    } else if (isMissing) {
        backgroundColor = isDark ? '#3a1a1a' : '#fff2f0'; // dark red / light red
    }

    const content = (
        <div style={{ 
            backgroundColor, 
            padding: '4px 8px', 
            borderRadius: '4px',
            textAlign: 'center',
            minHeight: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Text style={{ fontSize: '12px', fontWeight: isNative ? 'bold' : '500', color: isDark ? '#ffffff' : '#000000' }}>
                {valueDisplay}
            </Text>
        </div>
    );

    if (timestamp) {
        return (
            <Tooltip title={renderTimestampTooltip(timestamp, month)}>
                {content}
            </Tooltip>
        );
    }

    return content;
};

const renderWaterType = (isHot?: boolean | null) => {
    if (isHot === null || isHot === undefined) {
        return <Tag color="default">Unknown</Tag>;
    }

    return isHot ? <Tag color="volcano">Hot</Tag> : <Tag color="geekblue">Cold</Tag>;
};

export const HistoricalReadingsTable: React.FC<HistoricalReadingsTableProps> = ({
    media,
    concentratorID,
    concentratorName,
    searchValue
}) => {
    const hasAppliedInitialSearchFilter = useRef(false);
    const { mode } = useContext(ColorModeContext);
    const isDark = mode === 'dark';
    const navigate = useNavigate();
    const tableOptions = useMemo(
        () => ({
            resource: `concentrators/${concentratorID}/meters/${media}/historical`,
            pagination: {
                pageSize: 10,
            },
            syncWithLocation: false,
            filters: {
                mode: "server" as const,
                defaultBehavior: "replace" as const,
                initial: [
                    {
                        field: "search",
                        operator: "eq" as const,
                        value: searchValue,
                    },
                ],
            },
            sorters: {
                mode: "server" as const,
            },
            meta: {
                months: 12,
            },
        }),
        [concentratorID, media, searchValue]
    );

    const { tableProps, setFilters } = useTable<MeterWithHistory>(tableOptions);


    const data = tableProps.dataSource as MeterWithHistory[] || [];

    // Apply search filter when searchValue changes
    useEffect(() => {
        if (!hasAppliedInitialSearchFilter.current) {
            hasAppliedInitialSearchFilter.current = true;
            return;
        }

        setFilters([
            {
                field: "search",
                operator: "eq",
                value: searchValue, // Always send value, backend handles empty string as "show all"
            }
        ]);
    }, [searchValue, setFilters]);

    // Create columns dynamically based on historical months
    const generateColumns = (): { columns: ColumnsType<MeterWithHistory>; monthKeys: string[] } => {
        if (data.length === 0) return { columns: [], monthKeys: [] };

        const baseColumns: ColumnsType<MeterWithHistory> = [
            {
                title: 'Meter Name',
                dataIndex: 'name',
                key: 'name',
                fixed: 'left',
                width: 200,
                render: (name: string, record: MeterWithHistory) => (
                    <Space>
                        <span>{name}</span>
                        <ChartIconButton
                            concentratorID={concentratorID}
                            meterID={record.id}
                            onClick={() => {
                                navigate(`/concentrators/${concentratorID}/meters/${record.id}/chart`);
                            }}
                            style={{
                                color: '#1890ff',
                                padding: '2px 4px',
                                height: 'auto',
                                minWidth: 'auto'
                            }}
                        />
                    </Space>
                ),
            },
            {
                title: 'UUID',
                dataIndex: 'uuid',
                key: 'uuid',
                width: 150,
            }
        ];

        baseColumns.push(
            {
                title: 'Address',
                dataIndex: 'address',
                key: 'address',
                width: 150,
                render: (value: string | null) => value ? value : 'N/A',
            },
            {
                title: 'Serial',
                dataIndex: 'serial',
                key: 'serial',
                width: 160,
                render: (value: string | null) => value ? value : 'N/A',
            }
        );

        if (media === 'water') {
            baseColumns.push({
                title: 'Water Type',
                dataIndex: 'hot',
                key: 'hot',
                width: 110,
                render: (value: boolean | null) => renderWaterType(value),
            });
        }

        // Get all unique months from the first meter's historical readings
        const monthsSet = new Set<string>();
        data.forEach(meter => {
            meter.historical_readings.forEach(reading => {
                monthsSet.add(reading.month);
            });
        });

        const sortedMonths = Array.from(monthsSet).sort().reverse(); // Most recent first

        const monthColumns: ColumnsType<MeterWithHistory> = sortedMonths.map(month => ({
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span>{month}</span>
                    <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleCsvExport(concentratorID, media, month, concentratorName)}
                        style={{ 
                            minWidth: 'auto',
                            padding: '4px',
                            height: 'auto'
                        }}
                        title={`Export ${month} CSV`}
                    />
                </div>
            ),
            key: month,
            width: media === 'electricity' ? 180 : 140,
            render: (_: any, record: MeterWithHistory) => {
                const reading = record.historical_readings.find(r => r.month === month);
                if (!reading) {
                    return <Tag color="default">No Data</Tag>;
                }

                if (media === 'electricity') {
                    // For electricity, show both active and reactive energy in split cell
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '2px' }}>
                                {renderReadingCell(
                                    reading.active_value,
                                    reading.active_timestamp,
                                    reading.active_is_missing,
                                    'kWh',
                                    month,
                                    false,
                                    isDark
                                )}
                            </div>
                            <div style={{ paddingTop: '2px' }}>
                                {renderReadingCell(
                                    reading.reactive_value,
                                    reading.reactive_timestamp,
                                    reading.reactive_is_missing,
                                    'kVArh',
                                    month,
                                    false,
                                    isDark
                                )}
                            </div>
                        </div>
                    );
                } else if (media === 'heat') {
                    // For heat, show both kWh and GJ with native unit in bold
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '2px' }}>
                                {renderReadingCell(
                                    reading.value_kwh,
                                    reading.timestamp,
                                    reading.is_missing,
                                    'kWh',
                                    month,
                                    reading.native_unit === 'kWh',
                                    isDark
                                )}
                            </div>
                            <div style={{ paddingTop: '2px' }}>
                                {renderReadingCell(
                                    reading.value_gj,
                                    reading.timestamp,
                                    reading.is_missing,
                                    'GJ',
                                    month,
                                    reading.native_unit === 'GJ',
                                    isDark
                                )}
                            </div>
                        </div>
                    );
                } else {
                    // For water, show single value
                    const unit = 'm³';
                    return renderReadingCell(
                        reading.value,
                        reading.timestamp,
                        reading.is_missing,
                        unit,
                        month,
                        false,
                        isDark
                    );
                }
            }
        }));

        return { columns: [...baseColumns, ...monthColumns], monthKeys: sortedMonths };
    };

    const { columns, monthKeys } = generateColumns();
    const defaultColumns: PersistedColumnConfig[] = [
        { key: 'name', visible: true },
        { key: 'uuid', visible: false },
        { key: 'address', visible: false },
        { key: 'serial', visible: false },
        ...(media === 'water' ? [{ key: 'hot', visible: true }] : []),
    ];

    return (
        <div style={{ width: '100%' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <MeterReadingsTable<MeterWithHistory>
                    tableKey={`meters-${concentratorID}-${media}-historical`}
                    rowKey="id"
                    tableProps={tableProps}
                    columns={columns}
                    defaultColumns={defaultColumns}
                    lockedColumnKeys={monthKeys}
                    columnStateMode="url"
                    scroll={{
                        x: Math.max(
                            800,
                            350 +
                                (data.length > 0
                                    ? data[0]?.historical_readings?.length *
                                      (media === 'electricity' ? 160 : 120)
                                    : 0)
                        ),
                    }}
                    pagination={{
                        ...tableProps.pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                    }}
                />
            </Space>
        </div>
    );
};
